import type { AxiosInstance } from 'axios';
import { OktaAuth, AccessToken } from '@okta/okta-auth-js';

/**
 * Shared Okta token plumbing for every axios client in this bundle.
 *
 * There is more than one backend behind this frontend — Content Tracker at a
 * relative `/api`, Asset Library at its own origin — but only ever one signed-in
 * user. Holding the OktaAuth instance here rather than inside a single client
 * module means a new client gets the token interceptor by calling
 * `attachOktaToken`, and `setOktaAuth` still has exactly one call site in the
 * app bootstrap. Duplicating the interceptor per client would mean each new
 * backend needs its own wiring in `App.tsx` and would eventually drift.
 */

let oktaAuthInstance: OktaAuth | null = null;

/** Called once from the app bootstrap, for all clients. */
export const setOktaAuth = (oktaAuth: OktaAuth) => {
  oktaAuthInstance = oktaAuth;
};

/**
 * The current access token, or null if there is none.
 *
 * Never throws: a token read failing should degrade to an unauthenticated
 * request (which the backend rejects cleanly) rather than breaking the caller.
 */
export const getAccessToken = async (): Promise<string | null> => {
  if (!oktaAuthInstance) {
    if (import.meta.env.DEV) {
      console.warn('⚠ oktaAuthInstance not set yet - API call may fail');
    }
    return null;
  }

  try {
    const accessToken = (await oktaAuthInstance.tokenManager.get('accessToken')) as AccessToken;
    return accessToken?.accessToken ?? null;
  } catch (e) {
    if (import.meta.env.DEV) {
      console.warn('⚠ Failed to get Okta token', e);
    }
    return null;
  }
};

/** Attach the bearer-token request interceptor to an axios instance. */
export const attachOktaToken = (client: AxiosInstance) => {
  client.interceptors.request.use(async (config) => {
    const token = await getAccessToken();
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    if (import.meta.env.DEV) {
      console.log('→ API Request:', config.method?.toUpperCase(), config.url);
    }
    return config;
  });
};

/** Log API errors in development. Shared so each client doesn't restate it. */
export const attachErrorLogging = (client: AxiosInstance) => {
  client.interceptors.response.use(
    (response) => response,
    (error) => {
      if (import.meta.env.DEV) {
        console.error('API Error:', error.response?.status, error.response?.data);
      }
      return Promise.reject(error);
    },
  );
};
